import {
  QUEUE_PRIORITY,
  type SyncQueueItem,
  type SyncQueueType,
  type SyncStatus,
} from './db';
import { companyDb } from './companyContext';
import { useSyncStore } from '@/store/syncStore';

export async function enqueueSync(
  type: SyncQueueType,
  clientOfflineId: string,
  payload: Record<string, unknown>,
  offlineSource?: string,
): Promise<void> {
  const db = companyDb();
  const existing = await db.sync_queue.get(clientOfflineId);
  if (existing && existing.status === 'synced') return;

  const item: SyncQueueItem = {
    clientOfflineId,
    type,
    status: 'pending',
    payload,
    offlineSource,
    createdAt: new Date().toISOString(),
    retryCount: existing?.retryCount || 0,
    priority: QUEUE_PRIORITY[type],
    error: undefined,
  };
  await db.sync_queue.put(item);
  await refreshQueueCounts();
}

export async function refreshQueueCounts(): Promise<void> {
  const db = companyDb();
  const pending = await db.sync_queue.where('status').anyOf(['pending', 'syncing']).count();
  const failed = await db.sync_queue.where('status').equals('failed').count();
  useSyncStore.getState().setQueueCounts(pending, failed);
}

export async function listQueue(status?: SyncStatus | SyncStatus[]): Promise<SyncQueueItem[]> {
  const db = companyDb();
  if (!status) {
    return db.sync_queue.orderBy('priority').toArray();
  }
  const statuses = Array.isArray(status) ? status : [status];
  return db.sync_queue.where('status').anyOf(statuses).sortBy('priority');
}

export async function getPendingOrdered(): Promise<SyncQueueItem[]> {
  // Include stuck 'syncing' rows (interrupted drain / tab close) so Local First can retry them.
  const items = await companyDb()
    .sync_queue.where('status')
    .anyOf(['pending', 'syncing', 'failed'])
    .toArray();
  return items.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

export async function listFailedQueueItems(): Promise<SyncQueueItem[]> {
  const items = await companyDb().sync_queue.where('status').equals('failed').toArray();
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Remove failed queue rows only (local sales/stock stay in IndexedDB). */
export async function discardFailedQueueItems(): Promise<number> {
  const failed = await listFailedQueueItems();
  const db = companyDb();
  await db.transaction('rw', db.sync_queue, async () => {
    for (const item of failed) {
      await db.sync_queue.delete(item.clientOfflineId);
    }
  });
  await refreshQueueCounts();
  return failed.length;
}

/** Remove pending + syncing + failed queue rows (local docs stay on device). */
export async function discardAllQueueItems(): Promise<number> {
  const db = companyDb();
  const items = await db.sync_queue
    .where('status')
    .anyOf(['pending', 'syncing', 'failed'])
    .toArray();
  await db.transaction('rw', db.sync_queue, async () => {
    for (const item of items) {
      await db.sync_queue.delete(item.clientOfflineId);
    }
  });
  await refreshQueueCounts();
  return items.length;
}

export async function listPendingQueueItems(): Promise<SyncQueueItem[]> {
  const items = await companyDb()
    .sync_queue.where('status')
    .anyOf(['pending', 'syncing'])
    .toArray();
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function updateQueueItem(
  clientOfflineId: string,
  patch: Partial<SyncQueueItem>,
): Promise<void> {
  await companyDb().sync_queue.update(clientOfflineId, patch);
  await refreshQueueCounts();
}

export async function queueStatsByType(): Promise<Record<string, number>> {
  const pending = await getPendingOrdered();
  const stats: Record<string, number> = {};
  for (const item of pending) {
    stats[item.type] = (stats[item.type] || 0) + 1;
  }
  return stats;
}
